import {
    ChangeDetectionStrategy, Component, OnDestroy, OnInit,
    inject, signal, computed
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FieldExecutionService, BlockGroup } from '../../services/field-execution.service';
import { FieldExecutionImportService } from '../../services/field-execution-import.service';
import { ImportModalComponent } from './import-modal/import-modal.component';
import { Project } from '../../models/project.model';
import { AuthService } from '../../services/auth.service';
import { formatCostLakh, isFeDelayed, isFeInProgress, isFeOnTrack } from '../../utils/field-execution-stats.utils';

type ListFilter =
  | 'totalBlocks' | 'totalProjects' | 'onTrack' | 'inProgress'
  | 'delayed' | 'sanctioned' | 'avgProgress'
  | 'chipDelayed' | 'chipLowProgress'
  | 'executants';

type SortOption = 'highest-delay' | 'lowest-progress' | 'most-projects' | 'alphabetical';

@Component({
    selector: 'app-field-execution',
    imports: [CommonModule, FormsModule, ImportModalComponent],
    templateUrl: './field-execution.component.html',
    styleUrl: './field-execution.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldExecutionComponent implements OnInit, OnDestroy {
    private readonly router    = inject(Router);
    private readonly location  = inject(Location);
    private readonly svc       = inject(FieldExecutionService);
    private readonly importSvc = inject(FieldExecutionImportService);
    private readonly auth      = inject(AuthService);
    private readonly destroy$  = new Subject<void>();
    private timerHandle: any;

    currentTime     = signal(new Date());
    allBlockGroups  = signal<BlockGroup[]>([]);
    isLoading       = signal(true);
    searchTerm      = signal('');
    showImportModal = signal(false);
    sortBy          = signal<SortOption>('highest-delay');
    activeFilters   = signal<Set<string>>(new Set());

    // ── Filtered + sorted list ────────────────────────────────────────────
    filteredBlocks = computed<BlockGroup[]>(() => {
        const q       = this.searchTerm().toLowerCase().trim();
        const filters = this.activeFilters();
        const sort    = this.sortBy();

        let result = this.allBlockGroups().filter(b => {
            const matchSearch  = !q || b.block.toLowerCase().includes(q);
            const matchDelayed = !filters.has('delayed')     || b.delayedCount > 0;
            const matchLowProg = !filters.has('low-progress')|| (b.avgProgress < 30 && b.totalProjects > 0);
            return matchSearch && matchDelayed && matchLowProg;
        });

        switch (sort) {
            case 'highest-delay':
                result = [...result].sort((a, b) => b.delayedCount - a.delayedCount);
                break;
            case 'lowest-progress':
                result = [...result].sort((a, b) => a.avgProgress - b.avgProgress);
                break;
            case 'most-projects':
                result = [...result].sort((a, b) => b.totalProjects - a.totalProjects);
                break;
            case 'alphabetical':
                result = [...result].sort((a, b) => a.block.localeCompare(b.block));
                break;
        }
        return result;
    });

    // ── Global summary (always full dataset) ──────────────────────────────
    globalStats = computed(() => {
        const blocks        = this.allBlockGroups();
        const totalBlocks   = blocks.length;
        const totalProjects = blocks.reduce((s, b) => s + b.totalProjects, 0);
        const onTrack       = blocks.reduce((s, b) => s + b.onTrackCount, 0);
        const inProgress    = blocks.reduce((s, b) => s + b.inProgressCount, 0);
        const delayed       = blocks.reduce((s, b) => s + b.delayedCount, 0);
        const sanctioned    = blocks.reduce((s, b) => s + b.sanctionedAmount, 0);
        const avgProg       = totalProjects > 0
            ? blocks.reduce((s, b) => s + (b.avgProgress * b.totalProjects), 0) / totalProjects
            : 0;
        return {
            totalBlocks,
            totalProjects,
            onTrack,
            onTrackPercent:    totalProjects > 0 ? Math.round((onTrack    / totalProjects) * 100) : 0,
            inProgress,
            inProgressPercent: totalProjects > 0 ? Math.round((inProgress / totalProjects) * 100) : 0,
            delayed,
            delayedPercent:    totalProjects > 0 ? Math.round((delayed    / totalProjects) * 100) : 0,
            sanctioned,
            avgProgress:       Math.round(avgProg)
        };
    });

    // ── Chip badge counts ─────────────────────────────────────────────────
    filterCounts = computed(() => ({
        delayed:     this.allBlockGroups().filter(b => b.delayedCount > 0).length,
        lowProgress: this.allBlockGroups().filter(b => b.avgProgress < 30 && b.totalProjects > 0).length,
    }));

    // ── Executant aggregation ─────────────────────────────────────────────
    executantSearch = signal('');

    private allFlatProjects = computed<Project[]>(() =>
        this.allBlockGroups().flatMap(b => b.villages.flatMap(v => v.projects))
    );

    executantStats = computed(() => {
        const seen = new Set<string>();
        for (const p of this.allFlatProjects()) {
            const num  = (p.executorNumber ?? '').trim();
            const name = (p.executorName   ?? (p as any).executingAgency ?? '').trim();
            const key  = num ? `number:${num}` : name ? `name:${name}` : '';
            if (key) seen.add(key);
        }
        return { unique: seen.size };
    });

    executantList = computed(() => {
        // Group purely by executant name (case-insensitive).
        // Phone number is stored for display only — never used as the grouping key.
        type Entry = { key: string; name: string; number: string; projects: Project[] };
        const map = new Map<string, Entry>(); // keyed by canonical (lowercase) name

        for (const p of this.allFlatProjects()) {
            const rawName = (p.executorName ?? (p as any).executingAgency ?? '').trim();
            if (!rawName) continue;
            const canonical = rawName.toLowerCase();
            if (!map.has(canonical)) {
                map.set(canonical, {
                    key:     `name:${encodeURIComponent(rawName)}`,
                    name:    rawName,
                    number:  (p.executorNumber ?? '').trim(),
                    projects: []
                });
            }
            // Capture phone number from the first project that has one
            const entry = map.get(canonical)!;
            if (!entry.number) entry.number = (p.executorNumber ?? '').trim();
            entry.projects.push(p);
        }
        const q = this.executantSearch().toLowerCase().trim();
        return [...map.values()]
            .filter(e => !q || e.name.toLowerCase().includes(q) || e.number.includes(q))
            .sort((a, b) => b.projects.length - a.projects.length)
            .map(e => {
                const total    = e.projects.length;
                const delayed  = e.projects.filter(p => p.status === 'Stuck').length;
                const completed = e.projects.filter(p => p.status === 'Completed').length;
                const riskScore = total > 0 ? Math.round((delayed / total) * 100) : 0;
                const risk = riskScore >= 50 ? 'high' : riskScore >= 25 ? 'medium' : 'low';
                const riskLabel = riskScore >= 50 ? 'High' : riskScore >= 25 ? 'Medium' : 'Low';
                return { ...e, total, delayed, completed, riskScore, risk, riskLabel };
            });
    });

    // ── Dynamic list modal ────────────────────────────────────────────────
    listFilter = signal<ListFilter | null>(null);

    listTitle = computed(() => {
        switch (this.listFilter()) {
            case 'totalBlocks':     return 'All Blocks';
            case 'totalProjects':   return 'All Projects';
            case 'onTrack':         return 'On-Track Projects (≥ 70%)';
            case 'inProgress':      return 'In-Progress Projects (30%–70%)';
            case 'delayed':         return 'Delayed Projects (> 60 Days)';
            case 'sanctioned':      return 'Sanctioned Budget — All Projects';
            case 'avgProgress':     return 'Projects by Progress';
            case 'chipDelayed':     return 'Blocks with Delayed Projects';
            case 'chipLowProgress': return 'Blocks with Low Progress (< 30%)';
            case 'executants':      return 'All Executants';
            default:                return '';
        }
    });

    listShowsBlocks = computed(() =>
        this.listFilter() === 'totalBlocks' ||
        this.listFilter() === 'chipDelayed' ||
        this.listFilter() === 'chipLowProgress'
    );

    listShowsExecutants = computed(() => this.listFilter() === 'executants');

    listBlocks = computed<BlockGroup[]>(() => {
        const all = this.allBlockGroups();
        switch (this.listFilter()) {
            case 'totalBlocks':     return all;
            case 'chipDelayed':     return all.filter(b => b.delayedCount > 0);
            case 'chipLowProgress': return all.filter(b => b.avgProgress < 30 && b.totalProjects > 0);
            default:                return [];
        }
    });

    listProjects = computed<Project[]>(() => {
        const all = this.allFlatProjects();
        switch (this.listFilter()) {
            case 'totalProjects':
            case 'sanctioned':
            case 'avgProgress':  return all;
            case 'onTrack':      return all.filter(p => isFeOnTrack(p));
            case 'inProgress':   return all.filter(p => isFeInProgress(p));
            case 'delayed':      return all.filter(p => isFeDelayed(p));
            default:             return [];
        }
    });

    formatCostLakh = formatCostLakh;

    /** Compact sanctioned display for mobile KPI tile (lakhs, no suffix). */
    sanctionedCompact(): string {
        return Math.round(this.globalStats().sanctioned).toLocaleString('en-IN');
    }

    openList(filter: ListFilter): void {
        if (filter !== 'executants') this.executantSearch.set('');
        this.listFilter.set(filter);
    }
    closeList(): void { this.listFilter.set(null); this.executantSearch.set(''); }

    ngOnInit(): void {
        this.svc.getBlockGroups$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next:  groups => { this.allBlockGroups.set(groups); this.isLoading.set(false); },
                error: ()     => this.isLoading.set(false)
            });
        this.timerHandle = setInterval(() => this.currentTime.set(new Date()), 60000);
    }

    ngOnDestroy(): void {
        if (this.timerHandle) clearInterval(this.timerHandle);
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Navigation ────────────────────────────────────────────────────────
    goBack(): void { this.location.back(); }
    goHome(): void { this.router.navigate(['/home']); }

    selectBlock(block: BlockGroup): void {
        this.router.navigate(['/field-execution', block.block]);
    }

    navigateToExecutant(key: string): void {
        this.closeList();
        this.router.navigate(['/field-execution/executant', key], {
            state: { returnUrl: '/field-execution' },
        });
    }

    // ── Toolbar ───────────────────────────────────────────────────────────
    toggleFilter(filter: string): void {
        this.activeFilters.update(set => {
            const next = new Set(set);
            next.has(filter) ? next.delete(filter) : next.add(filter);
            return next;
        });
    }

    isFilterActive(filter: string): boolean {
        return this.activeFilters().has(filter);
    }

    setSortBy(value: string): void {
        this.sortBy.set(value as SortOption);
    }

    refreshData(): void {
        this.currentTime.set(new Date());
        // Firestore real-time subscription auto-updates data
    }

    clearFilters(): void {
        this.searchTerm.set('');
        this.activeFilters.set(new Set());
    }

    // ── Import ────────────────────────────────────────────────────────────
    onImportClosed(_: boolean): void { this.showImportModal.set(false); }

    downloadTemplate(): void { this.importSvc.downloadTemplate(); }

    // ── Block display label (OSD / Minister see "Dhamnagar Block" / "Tihidi Block") ──
    blockLabel(name: string): string {
        if (!this.auth.isMinisterOrOsd()) return name;
        if (name === 'Dhamanagar') return 'Dhamnagar Block';
        if (name === 'Tihidi')     return 'Tihidi Block';
        return name;
    }

    // ── UI helpers ────────────────────────────────────────────────────────
    statusLabel(status: string): string {
        const map: Record<string, string> = {
            'Completed': 'On Track',
            'In Progress': 'Work Started',
            'Stuck': 'Delayed',
            'Planned': 'Not Started',
        };
        return map[status] || status;
    }

    statusPillClass(badge?: string): string {
        if (!badge) return '';
        return 'status-pill ' + badge.toLowerCase().replace(/\s+/g, '-');
    }

    sideIndicatorClass(badge?: string): string {
        if (badge === 'High Delay')      return 'red';
        if (badge === 'Low Performance') return 'orange';
        if (badge === 'Good Progress')   return 'green';
        return '';
    }
}
